import { APP_PATHS } from '@/config/routes/app-paths';
import { isDashboardShellPath } from '@/config/routes/dashboard-paths';
import type { PageSkeletonKey } from '@/types/page-skeleton';

/**
 * Paths with no entry in `APP_PATHS` — the placeholder pages and the Admin Settings
 * sub-pages. Listed here rather than added to `APP_PATHS`, which doubles as the allow-list
 * `ensureValidPath` validates navigation targets against.
 */
const SKELETON_ONLY_PATHS = {
  settings: '/settings',
  branding: '/branding',
  numbers: '/numbers',
  reports: '/reports',
  rolesAdmin: '/admin-settings/roles-admin',
  apiKeysAdmin: '/admin-settings/api-keys-admin',
  permissionsAdmin: '/admin-settings/permissions-admin',
  serviceAdmin: '/admin-settings/service-admin',
  featureAdmin: '/admin-settings/feature-admin',
} as const;

const PAGE_SKELETON_BY_PATH: Record<string, PageSkeletonKey> = {
  [APP_PATHS.home]: 'landing',
  [APP_PATHS.signin]: 'authCard',
  [APP_PATHS.activate]: 'authCard',
  [APP_PATHS.pendingActivation]: 'authCard',
  [APP_PATHS.recoverAccount]: 'authCard',
  [APP_PATHS.overview]: 'overview',
  [APP_PATHS.billing]: 'billing',
  [APP_PATHS.services]: 'services',
  [APP_PATHS.team]: 'team',
  [APP_PATHS.wholesalerSettings]: 'wholesalerSettings',
  [APP_PATHS.adminSettings]: 'adminSettings',
  [APP_PATHS.setup]: 'setup',
  [SKELETON_ONLY_PATHS.settings]: 'placeholder',
  [SKELETON_ONLY_PATHS.branding]: 'placeholder',
  [SKELETON_ONLY_PATHS.numbers]: 'placeholder',
  [SKELETON_ONLY_PATHS.reports]: 'placeholder',
  [SKELETON_ONLY_PATHS.rolesAdmin]: 'rolesAdmin',
  [SKELETON_ONLY_PATHS.apiKeysAdmin]: 'apiKeysAdmin',
  [SKELETON_ONLY_PATHS.permissionsAdmin]: 'permissionsAdmin',
  [SKELETON_ONLY_PATHS.serviceAdmin]: 'adminDetail',
  [SKELETON_ONLY_PATHS.featureAdmin]: 'adminDetail',
};

// Longest first, so `/admin-settings/roles-admin` wins over `/admin-settings`, and a detail
// path like `/admin-settings/roles-admin/<id>` still resolves to its list page's shape.
const SKELETON_PATHS = Object.keys(PAGE_SKELETON_BY_PATH).sort((a, b) => b.length - a.length);

/**
 * Which loading shape stands in for `pathname` while its route resolves. A path the table
 * doesn't name falls back on the shell it renders in — a generic dashboard page, or the
 * centred card the public pages share.
 */
export const resolvePageSkeletonKey = (pathname: string): PageSkeletonKey => {
  const matched = SKELETON_PATHS.find(
    (path) => pathname === path || pathname.startsWith(`${path}/`),
  );

  if (matched == null) {
    return isDashboardShellPath(pathname) ? 'dashboard' : 'authCard';
  }

  return PAGE_SKELETON_BY_PATH[matched] ?? 'dashboard';
};
